/* eslint-disable react-hooks/exhaustive-deps */
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useCallback, useRef, useEffect, useMemo } from 'react';
import { jobApplicantsJobTnSelector, userSelector } from 'redux/selectors';
import { trackEvent } from 'helpers/analytics';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { getContactsColumnsKey, getContactsFiltersKey, getContactsViewKey } from 'helpers/storeKeys';
import { contactsSortOptions } from 'components/organisms/contactsFilters/defaultFilters';
import { useIsMobile } from 'hooks/useIsMobile';
import { getColumns, getContactHeader } from './useContactsTableRows';
import { useExportContactsCsv } from './useExportContactsCsv';
import { useContactsSort } from './useContactsSort';
import { useTableViewMobile } from './useTableViewMobile';
import { useUpdateCustomFields } from './useUpdateCustomFields';

export const useExtendContacts = ({
  data,
  customFields,
  isFetching,
  queryParams,
  isLoadingAI,
  listId,
  selectedRows,
  setSelectedRows,
  meta,
  onSetFilters,
  defaultFilters,
  activeNetwork,
  page,
  type,
  list,
  isLoading,
}) => {
  const isPublic = type === 'public';
  const isMobile = useIsMobile();
  const listIdToUse = isPublic ? listId : list?.id;

  const params = useParams();
  const history = useHistory();
  const { linkedinSync } = useSelector(userSelector);
  const viewKey = getContactsViewKey(activeNetwork, listIdToUse, isPublic);
  const [view, setView] = useLocalStorage(viewKey, isPublic && isMobile ? 'card' : 'table');

  const getPublicViewFromList = () => {
    if (!isPublic || !list?.shareableLayout) return null;
    return list.shareableLayout === 'cards_view' ? 'card' : 'table';
  };

  const fixedPublicView = getPublicViewFromList();
  const effectiveView = isPublic && fixedPublicView ? fixedPublicView : view;
  const hasContacts = data?.length > 0;
  const showSkeleton = (hasContacts && isFetching && queryParams.page === 1) || (hasContacts && isLoadingAI);
  const joinTnFromJB = useSelector(jobApplicantsJobTnSelector);

  const getDefaultColumns = useCallback(
    () =>
      getColumns({
        type,
        listId: listIdToUse,
        contactCount: meta?.queryTotal ?? 0,
        tnHidden: activeNetwork.tnHidden || !activeNetwork?.features?.includes('job_board'),
        joinTnFromJB: joinTnFromJB || activeNetwork?.isCompany,
        customFields,
      }),
    [type, listIdToUse, meta?.queryTotal, activeNetwork, joinTnFromJB, customFields],
  );

  const [selectedColumns, setSelectedColumns] = useLocalStorage(
    getContactsColumnsKey({
      activeNetwork,
      listIdToUse,
      type,
      joinTnFromJB,
      listId: listIdToUse,
      contactCount: meta?.queryTotal ?? 0,
      tnHidden: activeNetwork.tnHidden || !activeNetwork?.features?.includes('job_board'),
    }),
    getDefaultColumns(),
  );

  // Syncs custom fields in the local storage
  useUpdateCustomFields({
    customFields: customFields || [],
    selectedColumns,
    setSelectedColumns,
  });

  useEffect(() => {
    if (page !== 'list' && view === 'board') {
      setView('table');
    }
  }, [page]);

  const tableRef = useRef(null);
  const { onClickExportContacts, isLoading: isDownloadingCsv } = useExportContactsCsv({
    selectedRows,
    queryParams,
    contactIds: data?.map((contact) => contact.id),
    meta,
    type,
  });
  const filtersKey = getContactsFiltersKey(activeNetwork, listIdToUse, page);
  const [showFilters, setShowFilters] = useLocalStorage(filtersKey, !isPublic || !isMobile);

  useEffect(() => {
    if (page === 'list') {
      trackEvent('lists:view_list', {
        collectionId: activeNetwork.id,
        listId,
        objectType: 'people',
        listType: 'static',
        viewMode: view,
      });
    }
  }, [listId]);

  const onSort = useContactsSort({ onSetFilters, queryParams, activeNetwork, type, listId, page, list });
  const toolbarOptionChange = (tool, ...args) => {
    switch (tool) {
      case 'view':
        // Don't allow view changes for public lists
        if (isPublic) {
          return;
        }
        trackEvent('contacts:change_view_mode', {
          collectionId: activeNetwork.id,
          listId: type === 'public' ? listId : list?.id,
          selectedView: args[0].value,
          page,
        });
        setView(args[0].value);
        break;
      case 'filter':
        setShowFilters(!showFilters);
        break;
      case 'sort': {
        const sortBy = contactsSortOptions.find((option) => option.value === args[0].value);
        onSort({
          direction: args[0].direction,
          ...sortBy,
        });
        break;
      }
      case 'columns':
        trackEvent('contacts:change_columns', {
          collectionId: activeNetwork.id,
          page,
          operation: args[1],
          columnName: args[2].accessor,
        });
        setSelectedColumns(...args);
        break;
      case 'group':
        break;
      case 'export':
        onClickExportContacts();
        break;
      default:
        break;
    }
  };

  const onResetFilters = () => {
    onSetFilters({
      ...defaultFilters,
      ...(queryParams.filters?.sortBy ? { sortBy: queryParams.filters.sortBy } : {}),
      ...(queryParams.filters?.sortDirection ? { sortDirection: queryParams.filters.sortDirection } : {}),
    });
  };

  const onCreateList = () => {
    trackEvent('lists:create_list_click', {
      collection_id: activeNetwork.id,
      page: 'all_contacts',
      origin: 'filters_footer',
      object_type: 'people',
    });
    history.push(`/networks/${activeNetwork.slug}/contacts/lists/create-list${history.location.search}`, {
      filters: queryParams.filters,
    });
  };

  const paths = [
    {
      exact: true,
      route: '/networks/:activeNetwork/contacts/:contactId',
      goBack: () => history.push(`/networks/${activeNetwork.slug}/contacts${history.location.search}`),
    },
    {
      exact: true,
      route: '/networks/:activeNetwork/list/:listId/add-contacts/:contactId',
      goBack: () =>
        history.push(`/networks/${activeNetwork.slug}/list/${params.listId}/add-contacts${history.location.search}`),
    },
    {
      exact: true,
      route: '/networks/:activeNetwork/list/:listId/contacts/:contactId',
      goBack: () =>
        history.push(`/networks/${activeNetwork.slug}/list/${params.listId}/contacts${history.location.search}`),
    },
  ];

  const handleSelectAll = useCallback(() => {
    if (selectedRows.length === meta?.queryTotal) {
      setSelectedRows([]);
      return;
    }
    setSelectedRows(Array.from(Array(meta.queryTotal).keys()));
  }, [selectedRows, meta?.queryTotal, setSelectedRows]);

  useTableViewMobile({
    setSelectedColumns,
    columnDefinition: selectedColumns,
  });

  const publicViewColumns = useMemo(() => {
    if (!isPublic) return null;

    const fieldSettings = list?.shareableView?.fieldSettings;
    if (!fieldSettings || fieldSettings.length === 0) return null;

    const defaultCols = getDefaultColumns();
    const flattenedColumns = defaultCols.reduce((acc, column) => {
      acc[column.accessor] = column;
      return acc;
    }, {});

    // Handle 'all' value for backwards compatibility - show all fields (public view only)
    if (fieldSettings.includes('all')) {
      return defaultCols.map((col) => ({ ...col, show: true }));
    }

    const parsedColumns = fieldSettings.reduce((acc, field) => {
      if (field.visible && flattenedColumns[field.fieldId]) {
        acc.push({ ...flattenedColumns[field.fieldId], show: true });
      }
      return acc;
    }, []);

    const nameColumn = flattenedColumns.name;
    const hasNameColumn = parsedColumns.some((col) => col.accessor === 'name');
    if (nameColumn && !hasNameColumn) {
      parsedColumns.unshift({ ...nameColumn, show: true });
    }

    return parsedColumns.length > 0 ? parsedColumns : null;
  }, [isPublic, list?.shareableView, getDefaultColumns]);

  const cleanColumns = useMemo(() => {
    const columnsToUse = publicViewColumns || selectedColumns;

    const flattenedColumns = getDefaultColumns().reduce((acc, column) => {
      acc[column.accessor] = column;
      return acc;
    }, {});

    const result = columnsToUse.map((column) => {
      let header = column.accessor === 'name' ? getContactHeader(meta?.queryTotal ?? 0) : column.Header;
      if ((isLoadingAI || (isFetching && queryParams.page <= 1)) && column.accessor === 'name') {
        header = 'Loading...';
      }

      return {
        ...flattenedColumns[column.accessor],
        pinned: column.pinned,
        Header: header,
        show: column.show,
        width: column.width,
        ...(isPublic ? { editable: false, singleClickEdit: false } : {}),
      };
    });

    return result;
  }, [
    selectedColumns,
    publicViewColumns,
    getDefaultColumns,
    meta?.queryTotal,
    isLoadingAI,
    isFetching,
    isLoading,
    isPublic,
  ]);

  return {
    view: effectiveView,
    setView,
    showFilters,
    setShowFilters,
    toolbarOptionChange,
    onResetFilters,
    onCreateList,
    handleSelectAll,
    linkedinSync,
    showSkeleton,
    selectedColumns: cleanColumns,
    setSelectedColumns,
    tableRef,
    paths,
    hasContacts,
    isDownloadingCsv,
    currentSort: {
      sortBy: queryParams?.filters?.sortBy ?? 'relevance',
      sortDirection: queryParams?.filters?.sortDirection ?? 'asc',
    },
  };
};
